import axios from 'axios';

// API base URL - gets replaced in production
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

/**
 * Service for interacting with grants data
 */
class GrantService {
  /**
   * Search for grants based on criteria
   */
  async searchGrants(searchParams) {
    try {
      const response = await axios.post(`${API_BASE_URL}/search-grants`, searchParams);
      return response.data;
    } catch (error) {
      this.handleError(error, 'Error searching grants');
      throw error;
    }
  }

  /**
   * Get detailed information about a specific grant
   */
  async getGrantDetails(grantId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/grant/${grantId}`);
      return response.data;
    } catch (error) {
      this.handleError(error, 'Error fetching grant details');
      throw error;
    }
  }

  /**
   * Get list of all agencies/departments
   */
  async getAgencies() {
    try {
      const response = await axios.get(`${API_BASE_URL}/agencies`);
      return response.data.data;
    } catch (error) {
      this.handleError(error, 'Error fetching agencies');
      throw error;
    }
  }

  /**
   * Get list of all fiscal years
   */
  async getFiscalYears() {
    try {
      const response = await axios.get(`${API_BASE_URL}/fiscal-years`);
      return response.data.data;
    } catch (error) {
      this.handleError(error, 'Error fetching fiscal years');
      throw error;
    }
  }

  /**
   * Export search results to CSV
   */
  exportToCSV(grants) {
    if (!grants || !grants.length) {
      return '';
    }

    // Get headers from first grant object
    const headers = Object.keys(grants[0]);
    
    // Create CSV header row
    let csvContent = headers.join(',') + '\n';
    
    // Add data rows
    grants.forEach(grant => {
      const row = headers.map(header => {
        // Handle values that might contain commas
        const value = grant[header] === null ? '' : String(grant[header]);
        return `"${value.replace(/"/g, '""')}"`;
      });
      
      csvContent += row.join(',') + '\n';
    });
    
    return csvContent;
  }

  /**
   * Format currency values
   */
  formatCurrency(value) {
    if (!value) return '$0';
    
    // Remove any existing formatting
    const numericValue = typeof value === 'string' ? 
      parseFloat(value.replace(/[^0-9.-]+/g, '')) : 
      value;
    
    // Format as USD
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numericValue);
  }

  /**
   * Handle API errors
   */
  handleError(error, defaultMessage = 'An error occurred') {
    let errorMessage = defaultMessage;
    
    if (error.response) {
      const { data } = error.response;
      errorMessage = data.message || `Error ${error.response.status}: ${defaultMessage}`;
      console.error(errorMessage, data);
    } else if (error.request) {
      errorMessage = 'No response received from server';
      console.error(errorMessage, error.request);
    } else {
      errorMessage = error.message || defaultMessage;
      console.error(errorMessage, error);
    }
  }
}

// Export as singleton instance
export default new GrantService();